import {
  Globe2,
  HeartHandshake,
  Rocket,
  GraduationCap,
  Clock,
  Sparkles,
  ArrowRight,
  MapPin,
} from 'lucide-react';

import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import Container from './Main/sections/Container';

const CAREERS_EMAIL = 'careers@schoolhack.ai';

const PERKS = [
  { icon: Globe2, title: 'Remote-first', body: 'Work from anywhere. We hire the best people, wherever they are in the world.' },
  { icon: HeartHandshake, title: 'Real impact', body: 'Everything you ship reaches millions of students actively studying with School Hack.' },
  { icon: Rocket, title: 'Ownership', body: 'Small team, big autonomy. You own your work end-to-end and see it live fast.' },
  { icon: GraduationCap, title: 'Learning budget', body: 'An annual budget for courses, books, and conferences — we practise what we teach.' },
  { icon: Clock, title: 'Flexible hours', body: 'Async by default. Do your best work on a schedule that fits your life.' },
  { icon: Sparkles, title: 'Great team', body: 'Kind, curious, high-craft people who care about students and about each other.' },
];

const ROLES = [
  { title: 'Senior Frontend Engineer', team: 'Engineering', location: 'Remote', type: 'Full-time' },
  { title: 'AI / ML Engineer', team: 'Engineering', location: 'Remote', type: 'Full-time' },
  { title: 'Product Designer', team: 'Design', location: 'Remote', type: 'Full-time' },
  { title: 'Growth Marketer', team: 'Marketing', location: 'Remote', type: 'Full-time' },
  { title: 'Community Manager', team: 'Community', location: 'Remote', type: 'Full-time' },
  { title: 'Student Ambassador', team: 'Community', location: 'Global', type: 'Part-time' },
];

const Career = () => {
  return (
    <>
      <PageHero
        eyebrow="Careers"
        title="Build the Future of"
        titleAccent="Learning"
        subtitle="We're a small, ambitious team helping 3.5M+ students learn smarter. Come help us build the study tools we all wish we had."
      />

      {/* perks */}
      <section className="relative py-14 lg:py-16">
        <Container>
          <Reveal className="text-center">
            <h2 className="font-heading text-[1.7rem] font-bold text-white sm:text-[2.2rem]">
              Why you&apos;ll love it here
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {PERKS.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 90}>
                <div className="glass glass-hover h-full rounded-2xl p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/12 text-accent">
                    <p.icon size={21} />
                  </span>
                  <h3 className="font-heading mt-5 text-[1.1rem] font-semibold text-white">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-[0.87rem] leading-relaxed text-white/55">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* open roles */}
      <section className="relative py-14 lg:py-20">
        <div className="glow left-1/2 top-0 h-[360px] w-[600px] -translate-x-1/2 bg-accent/8" />
        <Container className="relative">
          <Reveal className="text-center">
            <span className="eyebrow">Open Roles</span>
            <h2 className="font-heading mx-auto mt-5 max-w-2xl text-[2rem] font-bold leading-[1.1] text-white sm:text-[2.5rem]">
              <span className="head-fade">Find Your </span>
              <span className="text-accent-grad">Place</span>
            </h2>
          </Reveal>

          <div className="mx-auto mt-10 flex max-w-3xl flex-col gap-3">
            {ROLES.map((r, i) => (
              <Reveal key={r.title} delay={i * 60}>
                <a
                  href={`mailto:${CAREERS_EMAIL}?subject=Application: ${r.title}`}
                  className="glass glass-hover group flex flex-col gap-3 rounded-2xl p-5 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <h3 className="font-heading text-[1.1rem] font-semibold text-white group-hover:text-accent">
                      {r.title}
                    </h3>
                    <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[0.8rem] text-white/45">
                      <span>{r.team}</span>
                      <span className="flex items-center gap-1">
                        <MapPin size={12} /> {r.location}
                      </span>
                      <span className="rounded-full bg-accent/12 px-2 py-0.5 text-accent">{r.type}</span>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[0.85rem] font-semibold text-accent">
                    Apply
                    <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-12 text-center">
            <p className="text-[0.92rem] text-white/55">
              Don&apos;t see your role? We&apos;re always keen to meet great people.
            </p>
            <a
              href={`mailto:${CAREERS_EMAIL}?subject=General Application`}
              className="btn-ghost mt-5 px-6 py-3 text-[0.9rem]"
            >
              Send an open application
              <ArrowRight size={16} />
            </a>
          </Reveal>
        </Container>
      </section>
    </>
  );
};

export default Career;
